<template>
  <div class="flexben-container">
    <!-- Header -->
    <div class="row mb-3 align-items-center">
      <div class="col-12 col-md-6 flexben-page-title ">
        <a class="btn text-themed go-back ms-2" @click="goBack()">
          <i class="bi bi-arrow-left me-1"></i>{{ $t("flexben.general.go_back") }}
        </a>
        <h1 class="mb-0" v-if="formAction === 'create'">{{ $t("superUpLang.widget.new") }}</h1>
        <h1 class="mb-0" v-else>{{ $t("superUpLang.widget.update") }}</h1>
      </div>
    </div>

    <!-- Step 1: Tip + Provider + Slug -->
    <div class="container-bg-white p-3">
      <div class="row g-3">
        <div class="col-md-4">
          <div class="form-group required mb-1">
            <label class="col-form-label pt-0">{{ $t("superUpLang.widget.form_actions.select_type") }}</label>
            <multiselect
              :disabled="id !== null"
              v-model="formData.type"
              :options="types"
              :placeholder="$t('selectLabel')"
              track-by="id"
              label="name"
            />
          </div>
        </div>

        <div class="col-md-4">
          <div class="form-group required mb-1">
            <label class="col-form-label pt-0">{{ $t("superUpLang.widget.form_actions.select_provider") }}</label>
            <multiselect
              :disabled="!formData.type || id !== null"
              v-model="formData.provider"
              :options="providers"
              :placeholder="$t('selectLabel')"
              track-by="id"
              label="name"
            />
          </div>
        </div>

        <div class="col-md-4" v-if="formData.provider">
          <div class="form-group required mb-1">
            <label class="col-form-label pt-0">{{ $t("superUpLang.widget.form_actions.select_id") }}</label>
            <input
              type="text"
              :disabled="id !== null"
              v-model="slug"
              class="form-control-widget form-control-widget-border"
            >
            <small class="text-muted d-block mt-1">* {{ $t("superUpLang.widget.form_actions.select_id_info") }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 2: Items -->
    <div class="mt-4 container-bg-white p-3">
      <!-- Sticky actions bar -->
      <div class="sticky-actions d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div class="text-muted small">
          <i class="bi bi-arrows-move me-1"></i>Drag pentru reordonare
        </div>
        <div class="d-flex gap-2">
          <button :disabled="!canSave" class="btn btn-success px-4" @click="submitForm">
            <i class="bi bi-check2 me-1"></i>{{ $t("superUpLang.widget.form_actions.save") }}
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="rows.length === 0" class="py-5">
        <div class="d-flex justify-content-center">
          <div
            class="add-card-tile card d-flex align-items-center justify-content-center text-center"
            @click="addRow()"
            role="button"
            tabindex="0"
            @keyup.enter="addRow()"
          >
            <div class="pl-4 pr-4">
              <button
                :disabled="!formData.type || !formData.provider"
                class="btn btn-themed px-4 fw-semibold mb-1">{{ $t("superUpLang.widget.form_actions.add_row") }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Draggable cards -->
      <draggable
        v-else
        v-model="rows"
        handle=".drag-handle-widget"
        tag="div"
        class="row g-2 mt-1"
        @change="onRowsChange"
      >
        <div
          v-for="(row, index) in rows"
          :key="row.id ? `row-${row.id}` : `new-${index}`"
          class="col-12 col-sm-6 col-md-4 col-lg-3"
        >
          <div class="widget-card card p-2" :class="getRowClass(row)">
            <!-- Header -->
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="d-flex align-items-center gap-2">
                <span class="drag-handle-widget small" title="Drag">&#x2630;</span>
                <div class="fw-semibold small">#{{ row.id || (index+1) }}</div>
              </div>
              <div class="d-flex align-items-center gap-1">
                <span
                  class="badge badge-compact"
                  :class="row.is_active ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                  {{ row.is_active ? $t('flexben.offers.statuses.active') : $t('flexben.offers.statuses.inactive') }}
                </span>
                <button class="btn btn-danger btn-xs" @click="removeRow(index, row)" aria-label="Remove">×</button>
              </div>
            </div>

            <!-- Product -->
            <div class="mb-2">
              <label class="label-input d-block small mb-1">{{ $t("superUpLang.widget.form_actions.select_product")
                }}</label>
              <multiselect
                class="multiselect-compact"
                :disabled="!formData.type"
                v-model="row.product"
                :options="products"
                :placeholder="$t('selectLabel')"
                track-by="id"
                label="name"
                @input="validateRowAt(index)"
              />
              <div class="tiny text-danger" v-if="rowErrors[index] && rowErrors[index].product">
                {{ rowErrors[index].product }}
              </div>
            </div>

            <!-- Active switch -->
            <div class="d-flex align-items-center justify-content-between mb-2">
              <div class="form-check form-switch m-0">
                <input class="form-check-input" type="checkbox" v-model="row.is_active" :id="'active_'+index">
                <label class="form-check-label small" :for="'active_'+index">
                  {{ row.is_active ? $t("flexben.offers.statuses.active") : $t("flexben.offers.statuses.inactive") }}
                </label>
              </div>
            </div>

            <!-- Config ID -->
            <div class="form-group required mb-1">
              <label class="label-input d-block small mb-1">{{ $t("superUpLang.widget.form_actions.select_config_id")}}</label>
              <multiselect
                class="multiselect-compact"
                :disabled="!formData.type"
                v-model="row.config_id"
                :options="configIds"
                :placeholder="$t('selectLabel')"
                track-by="id"
                label="name"
                :custom-label="o => o ? `${o.name} (${o.id})${o.description ? ' – ' + o.description : ''}` : ''"
                @input="validateRowAt(index)"
              />
              <div class="tiny text-danger" v-if="rowErrors[index] && rowErrors[index].config_id">
                {{ rowErrors[index].config_id }}
              </div>
            </div>

            <!-- Image -->
            <div class="mb-1">
              <label class="label-input d-block small mb-1">{{ $t("superUpLang.widget.form_actions.select_image")
                }}</label>
              <vue2Dropzone
                :ref="'imageUrlRef_' + index"
                :id="'imageUrl_'+index"
                class="dropzone-compact"
                @vdropzone-success="(file, response) => { onImageSuccess(file, response, row); validateRowAt(index) }"
                :options="{
                  url: `${ApiHelper().getBaseUrl('v1')}image-check`,
                  thumbnailWidth: 300,
                  addRemoveLinks: true,
                  autoProcessQueue: true
                }"
              />
              <div class="tiny text-danger" v-if="rowErrors[index] && rowErrors[index].image">
                {{ rowErrors[index].image }}
              </div>
            </div>
          </div>
        </div>

        <!-- Tile add card -->
        <div class="col-12 col-sm-6 col-md-4 col-lg-3" v-if="rows.length > 0">
          <div
            class="add-card-tile card d-flex align-items-center justify-content-center text-center h-100"
            @click="addRow()"
            role="button"
            tabindex="0"
            @keyup.enter="addRow()"
          >
            <div class="pl-4 pr-4">
              <button
                :disabled="!formData.type || !formData.provider"
                class="btn btn-themed px-4 fw-semibold mb-1">{{ $t("superUpLang.widget.form_actions.add_row") }}
              </button>
            </div>
          </div>
        </div>

        <div ref="lastScroll"></div>
      </draggable>
    </div>

    <content-loader :loader-visible="loading" :loader-error="loaderError"/>
  </div>
</template>

<script>
import HttpWidgetServices from "@/superUp-module/resources/services/httpWidgetServices";
import draggable from "vuedraggable";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Multiselect from "vue-multiselect";
import ApiHelper from "@/multiben-module/src/services/helper/apiHelper";

export default {
  name: "WidgetCreateUpdate",
  components: {draggable, vue2Dropzone, Multiselect},
  props: {formAction: {type: [String, null], required: true}, id: {type: [String, null], required: false}},
  data() {
    return {
      loaderError: false,
      loading: false,
      types: [],
      providers: [],
      products: [],
      configIds: [],
      formData: {type: null, provider: null},
      slug: "",
      rows: [],
      errors: {image: null},
      rowErrors: []
    };
  },
  computed: {
    canSave() {
      if (this.rows.length === 0) return false;
      return this.rowErrors.every(e => e && !e.product && !e.config_id && !e.image);
    }
  },
  methods: {
    ApiHelper() {
      return ApiHelper;
    },
    getRowClass(row) {
      if (this.formAction !== "update") return "";
      return row.is_active ? "row-highlight-widget border-success-widget" : "row-highlight-widget border-danger-widget";
    },
    loadExistingImage(row, index) {
      const refVal = this.$refs["imageUrlRef_" + index];
      const dzComp = Array.isArray(refVal) ? refVal[0] : refVal;
      if (!dzComp || !row.image) return;
      const dz = dzComp.dropzone;
      const mockFile = {name: (row.image.url || "").split("/").pop(), size: 60, type: "image/jpeg", accepted: true};
      dz.emit("addedfile", mockFile);
      dz.emit("thumbnail", mockFile, row.image.url);
      dz.emit("complete", mockFile);
      dz.files.push(mockFile);
    },
    goBack() {
      this.loaderError = false;
      this.loading = false;
      this.types = [];
      this.providers = [];
      this.products = [];
      this.rows = [];
      this.slug = "";
      this.formData.type = null;
      this.formData.provider = null;
      this.errors.image = null;
      this.rowErrors = [];
      this.$emit("update:formAction", null);
    },
    onImageSuccess(file, _response, row) {
      this.$set(row, "image", file);
    },
    addRow(item = {}) {
      if (!this.formData.type || !this.formData.provider) return;
      const newRow = item.product !== undefined ? {
        id: item.id,
        product: item.product,
        is_active: item.is_active,
        is_primary: item.is_primary,
        image: item.image,
        inactive_reason: item.inactive_reason,
        // păstrăm provizoriu string-ul; îl mapăm la obiect după ce avem this.configIds
        config_id: (item.data && item.data.config_id) ? item.data.config_id : null
      } : {
        id: null,
        product: {},
        is_active: true,
        is_primary: true,
        image: null,
        inactive_reason: null,
        config_id: null
      };
      this.rows.push(newRow);
      this.rowErrors.push(this.validateRow(newRow));
      this.$nextTick(() => {
        const el = this.$refs.lastScroll;
        if (el && el.scrollIntoView) el.scrollIntoView({behavior: "smooth", block: "end"});
      });
    },
    removeRow(index, row) {
      if (row.id) {
        HttpWidgetServices.deleteWidgetItem(row.id).then(() => {
          this.rows.splice(index, 1);
          this.rowErrors.splice(index, 1);
        }).catch(err => console.log(err));
      } else {
        this.rows.splice(index, 1);
        this.rowErrors.splice(index, 1);
      }
    },
    validateRow(row) {
      const errs = {product: null, config_id: null, image: null};
      if (!row.product || !row.product.id) {
        errs.product = this.$t("superUpLang.widget.errors.product_required");
      }
      // acum așteptăm un obiect {id, name,...} în row.config_id
      if (!row.config_id || !row.config_id.id) {
        errs.config_id = this.$t("superUpLang.widget.errors.config_id_required");
      } else if (!/^[a-zA-Z0-9_\-:.]+$/.test(String(row.config_id.id))) {
        errs.config_id = this.$t("superUpLang.widget.errors.config_id_format");
      }
      if (!row.image) {
        errs.image = this.$t("superUpLang.widget.errors.image_required");
      }
      return errs;
    },
    validateRowAt(index) {
      const row = this.rows[index];
      if (!row) return;
      this.$set(this.rowErrors, index, this.validateRow(row));
    },
    validateAll() {
      this.rowErrors = this.rows.map(r => this.validateRow(r));
    },
    onRowsChange() {
      // când se reordonează, resync erorile cu noua ordine
      this.validateAll();
    },
    submitForm() {
      this.validateAll();
      if (!this.canSave) return;
      this.loading = true;
      const formData = new FormData();
      formData.append("type", this.formData.type.id);
      formData.append("provider", this.formData.provider.id);
      formData.append("slug", this.slug);
      this.rows.forEach((row, index) => {
        formData.append(`products[${index}][product_id]`, row.product.id);
        formData.append(`products[${index}][image]`, row.image.id !== undefined ? JSON.stringify(row.image) : row.image);
        formData.append(`products[${index}][is_active]`, row.is_active);
        formData.append(`products[${index}][is_primary]`, row.is_primary);
        formData.append(`products[${index}][data]`, JSON.stringify({
          config_id: row.config_id ? row.config_id.id : ""
        }));
      });
      const service = this.formAction === "update"
        ? HttpWidgetServices.update(this.id, formData)
        : HttpWidgetServices.create(formData);
      service.then(() => {
        this.loading = false;
        this.$emit("update:formAction", null);
      }).catch(error => {
        this.loaderError = true;
        console.log(error);
      });
    },
    setProvider(typeId) {
      this.loading = true;
      HttpWidgetServices.getProviders(typeId).then(r => {
        this.providers = r.data;
        this.loading = false;
      }).catch(e => {
        this.loaderError = true;
        console.log(e);
      });
    },
    setProducts(provider) {
      this.loading = true;
      HttpWidgetServices.getProducts(this.formData.type.id, provider).then(r => {
        this.products = r.data;
        this.loading = false;
      }).catch(e => {
        this.loaderError = true;
        console.log(e);
      });
    },
    setConfigIds() {
      this.loading = true;
      return HttpWidgetServices.getConfigIds().then(r => {
        this.configIds = r.data;

        // mapăm orice valoare string existentă la obiectul opțiune
        this.rows = this.rows.map(row => {
          if (row && row.config_id && typeof row.config_id === "string") {
            const found = this.configIds.find(o => o.id === row.config_id);
            this.$set(row, "config_id", found || null);
          }
          return row;
        });

        // IMPORTANT: refacem validarea după mapare
        this.validateAll();
        this.loading = false;
      }).catch(e => {
        this.loaderError = true;
        this.loading = false;
        console.log(e);
      });
    }
  },
  watch: {
    formData: {
      handler(val) {
        if (this.id !== null) return;
        if (val.type) this.setProvider(val.type.id);
        if (val.provider) this.setProducts(val.provider.id);
      },
      deep: true
    }
  },
  mounted() {
    this.loading = true;

    if (this.formAction === "create") {
      Promise.all([
        HttpWidgetServices.getTypes().then(r => { this.types = r.data; }).catch(e => { this.loaderError = true; console.log(e); }),
        this.setConfigIds()
      ]).then(() => {
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
      return;
    }

    // UPDATE
    HttpWidgetServices.get(this.id).then(r => {
      this.formData.type = r.data.type;
      this.formData.provider = r.data.provider;
      this.slug = r.data.slug;

      this.setProducts(r.data.provider.id);
      Object.keys(r.data.items).forEach(k => this.addRow(r.data.items[k]));
      this.setConfigIds(); // mapează string -> obiect + revalidează

      this.$nextTick(() => {
        this.rows.forEach((row, i) => {
          if (row.image) this.loadExistingImage(row, i);
        });
      });
      this.loading = false;
    }).catch(e => {
      this.loaderError = true;
      console.log(e);
    });
  }
};
</script>

<style>
/* Carduri mici, tip tile */
.widget-card {
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, .05);
  height: 100%;
}

/* Tile gri Add card (aceeași amprentă ca un card) */
.add-card-tile {
  height: 100%;
  min-height: 210px; /* aprox. cât un card scurt */
  border: 1px dashed #d9d9d9;
  background: #f7f7f8;
  color: #6b7280;
  cursor: pointer;
  transition: all .15s ease;
}

.add-card-tile:hover {
  background: #f1f1f3;
  border-color: #cfd2d6;
  transform: translateY(-1px);
}

/* Dimensiuni compacte */
.badge-compact {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
}

.btn-xs {
  padding: 2px 6px;
  font-size: 12px;
  line-height: 1;
}

.tiny {
  font-size: 12px;
}

/* Dropzone compact */
.dropzone-compact.dropzone {
  padding: 6px;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  min-height: 110px;
}

.dropzone-compact .dz-preview {
  margin: 6px 0 0 0;
}

.dropzone-compact .dz-preview .dz-image img {
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
}

/* multiselect compact */
.multiselect-compact .multiselect__tags {
  min-height: 34px;
  padding: 4px 34px 4px 8px;
}

.multiselect-compact .multiselect__single,
.multiselect-compact .multiselect__input,
.multiselect-compact .multiselect__placeholder {
  line-height: 24px;
  font-size: 13px;
}

/* Grid cards mici */
.widget-card {
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, .05);
  height: 100%;
}

.badge-compact {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
}

.btn-xs {
  padding: 2px 6px;
  font-size: 12px;
  line-height: 1;
}

.tiny {
  font-size: 12px;
}

/* Dropzone mai mică, preview mic */
.dropzone-compact.dropzone {
  padding: 6px;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  min-height: 110px;
}

.dropzone-compact .dz-preview {
  margin: 6px 0 0 0;
}

.dropzone-compact .dz-preview .dz-image img {
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
}

/* multiselect compact */
.multiselect-compact .multiselect__tags {
  min-height: 34px;
  padding: 4px 34px 4px 8px;
}

.multiselect-compact .multiselect__single,
.multiselect-compact .multiselect__input,
.multiselect-compact .multiselect__placeholder {
  line-height: 24px;
  font-size: 13px;
}

/* General tweaks */
.row-highlight-widget {
  transition: background 0.3s ease;
}

.drag-handle-widget {
  cursor: grab;
  font-size: 18px;
  user-select: none;
}

.drag-handle-widget:active {
  cursor: grabbing;
}

input.form-control-widget {
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 14px;
  margin: 0 !important;
}

/* Status highlight when în update */
.border-success-widget {
  background: linear-gradient(90deg, #ffffff 0%, #e9f9ef 95%, #e3f7eb 100%);
}

.border-danger-widget {
  background: linear-gradient(90deg, #ffffff 0%, #fbe9ec 95%, #f8dde1 100%);
}

/* Cards */
.widget-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .04);
}

.widget-card .badge {
  font-weight: 600;
}

/* Compact dropzone */
.dropzone-compact.dropzone {
  padding: 8px;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}

.dropzone-compact .dz-preview {
  margin: 8px 0 0 0;
}

.dropzone-compact .dz-preview .dz-image img {
  width: 100%;
  height: 140px;
  object-fit: cover;
  border-radius: 6px;
}

/* Sticky top action bar (inside the white container) */
.sticky-actions {
  position: sticky;
  top: 56px; /* ajustează dacă ai navbar fix */
  z-index: 5;
  background: #fff;
  padding: 8px 0 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

/* Small helpers */
.form-group > span {
  color: #dc3545;
}

.label-input {
  font-size: 0.95rem;
  font-weight: 500;
}
</style>
